// Components
import EntitySearch from 'components/mrp/components/EntitySearch';
import { Popover } from 'components/mrp/components/Popover';

// types
import type { EmptyQuerySearchOptions } from 'components/mrp/components/EntitySearch';
import type {
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated/index';

// hooks
import { useEntityListener } from 'components/mrp/mrp.hooks';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// stores/context
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { ShortcutContext } from 'components/shared/ShortcutProvider/ShortcutContext';
import { useShallow } from 'zustand/shallow';
import { useCalendarInteractionStore } from '../../../store/calendarInteractionStore.store';
import { useCalendarNavigationStore } from '../../../store/calendarNavigationStore.store';

// utils
import { getPlanningEntityIdentifiersFromEvent } from 'components/mrp/components/Calendar/components/CalendarAppointment/CalenderAppointment.utils';
import { addEntityToEvent } from './EntityQuickConnect.utils';

// dialog
import { EntityType } from 'components/mrp/mrp.consts';
import { DialogContext } from 'components/shared/DialogProvider/DialogContext';
import { DialogType } from 'components/shared/DialogProvider/DialogProvider.enums';

const EntityQuickConnect = () => {
  'use memo';
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const {
    showEntityQuickConnect,
    eventRef,
    setShowEntityQuickConnect,
    setIsProcessing
  } = useCalendarInteractionStore(
    useShallow((state) => ({
      showEntityQuickConnect: state.showEntityQuickConnect,
      eventRef: state.eventRef,
      isProcessing: state.isProcessing,
      setShowEntityQuickConnect: state.control.setShowEntityQuickConnect,
      setIsProcessing: state.control.setIsProcessing
    }))
  );

  const { setActiveShortcutContexts, shortcuts } = useContext(ShortcutContext);

  useEffect(() => {
    setActiveShortcutContexts?.(
      !!showEntityQuickConnect ? [] : Object.keys(shortcuts)
    );
  }, [setActiveShortcutContexts, shortcuts, showEntityQuickConnect]);

  const event = useCalendarNavigationStore((state) => state.event);

  const calendarsObj = useCalendarStore((state) => state.calendars);
  const calendars = Object.values(calendarsObj);

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    listen: false,
    planningEntityIdentifiers: []
  });

  const emptyQueryOptions: EmptyQuerySearchOptions = {
    groupLabel: t('openedCalendars'),
    options: calendars
      .flatMap(({ sources }) => sources)
      .map((calendarData) => ({
        id: calendarData.planningEntityIdentifier.entityId!,
        label: calendarData.label,
        group: t(`openedCalendars`),
        isLocal: true,
        entity: calendarData.planningEntityIdentifier
      }))
  };

  const activeEntities = event?.planningEvent
    ? getPlanningEntityIdentifiersFromEvent(event.planningEvent).map(
        ({ entityId }) => entityId!
      )
    : [];

  const defaultEntityTypes = Object.values(EntityType);
  const entityFilters: PlanningEntityType[] =
    !event || !event.planningEvent || event.planningEvent.workPlace == null
      ? defaultEntityTypes
      : defaultEntityTypes.filter((type) => type !== 'WORKPLACE');

  const { openDialog, onClose } = useContext(DialogContext);

  const [fetchEntities, setFetchEntities] = useState<
    PlanningEntityIdentifier[]
  >([]);

  const onSelectEntity = (entity: PlanningEntityIdentifier) => {
    if (!event) return;

    setShowEntityQuickConnect(false);
    setIsProcessing(true);

    const formData = addEntityToEvent({
      event: event.planningEvent,
      entity
    });

    setFetchEntities([
      ...getPlanningEntityIdentifiersFromEvent(event.planningEvent),
      entity
    ]);

    openDialog?.(
      'smart-action-dialog-quickconnect-update',
      DialogType.SMART_ACTION_DIALOG,
      {
        actionType: 'update',
        appointmentExceptions: formData?.appointmentExceptions,
        payload: formData
      }
    );
  };

  useEffect(() => {
    onClose?.(
      'smart-action-dialog-quickconnect-update',
      DialogType.SMART_ACTION_DIALOG,
      async (props) => {
        if (props?.ok) {
          try {
            await refetchByPlanningEntityIdentifiers(fetchEntities);
          } catch (error) {
            console.error('Failed to refetch planning entities', error);
          }
        }

        setIsProcessing(false);
      }
    );
  }, [
    fetchEntities,
    onClose,
    refetchByPlanningEntityIdentifiers,
    setIsProcessing
  ]);

  return (
    <Popover
      id={`entity-quick-connect`}
      forceComponentWidth
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      useTransition={false}
      useForcedState={true}
      forceOpen={showEntityQuickConnect}
      onClose={() => setShowEntityQuickConnect(false)}
      childRef={eventRef}
      component={
        <EntitySearch
          activeEntities={activeEntities}
          entityTypeFilters={entityFilters}
          emptyQuerySearchOptions={emptyQueryOptions}
          minWidth={250}
          mode="compact"
          onChange={onSelectEntity}
          onEscape={() => setShowEntityQuickConnect(false)}
        />
      }
    />
  );
};

export default EntityQuickConnect;
